import { type WorkRequestId, workRequestIdSchema } from "./workIds";

export type RequestsPageView = {
  request?: WorkRequestId;
  view?: "all" | "pending" | "approved" | "done" | "rejected" | "unassigned";
  search?: string;
  asset?: string[];
  priority?: string[];
  status?: string[];
  requester?: string[];
  submittedFrom?: string;
  submittedTo?: string;
  requestPages?: number;
};

export function getWorkRequestDeepLink(
  currentUrl: string,
  externalId: unknown,
): string | null {
  const url = new URL(currentUrl);
  const idFromPath = url.pathname.split("/").filter(Boolean).at(-1);
  const parsedId = [externalId, idFromPath, url.searchParams.get("request")]
    .map((candidate) => workRequestIdSchema.safeParse(candidate))
    .find((candidate) => candidate.success);
  if (!parsedId?.success) return null;

  url.pathname = `/requests/${encodeURIComponent(parsedId.data)}`;
  url.searchParams.delete("request");
  url.hash = "";
  return url.toString();
}

function parseList(value: unknown): string[] | undefined {
  const values = Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : typeof value === "string"
      ? value.split(",")
      : [];
  const entries = values.map((entry) => entry.trim()).filter(Boolean);
  return entries.length > 0 ? entries : undefined;
}

function parseDate(value: unknown): string | undefined {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? value
    : undefined;
}

// Unknown keys are dropped, so links from before a filter was retired (such
// as `location`) still load and simply ignore it.
export function parseRequestsPageView(
  search: Record<string, unknown>,
): RequestsPageView {
  const request = workRequestIdSchema.safeParse(search.request);
  const view = search.view;
  return {
    request: request.success ? request.data : undefined,
    view:
      view === "pending" ||
      view === "approved" ||
      view === "done" ||
      view === "rejected" ||
      view === "unassigned"
        ? view
        : undefined,
    search:
      typeof search.search === "string"
        ? search.search.slice(0, 120)
        : undefined,
    asset: parseList(search.asset),
    priority: parseList(search.priority),
    status: parseList(search.status),
    requester: parseList(search.requester),
    submittedFrom: parseDate(search.submittedFrom),
    submittedTo: parseDate(search.submittedTo),
    requestPages: (() => {
      const pages = Number(search.requestPages);
      return Number.isSafeInteger(pages) && pages >= 1 ? pages : undefined;
    })(),
  };
}

export function mergeRequestsPageView(
  current: RequestsPageView,
  patch: Partial<RequestsPageView>,
): RequestsPageView {
  const resetsPages = [
    "view",
    "search",
    "asset",
    "priority",
    "status",
    "requester",
    "submittedFrom",
    "submittedTo",
  ].some((key) => key in patch);
  const next = {
    ...current,
    ...patch,
    ...(resetsPages ? { requestPages: undefined } : {}),
  };
  if (!next.request) delete next.request;
  if (!next.view || next.view === "all") delete next.view;
  if (!next.search) delete next.search;
  if (!next.asset) delete next.asset;
  if (!next.priority?.length) delete next.priority;
  if (!next.status?.length) delete next.status;
  if (!next.requester?.length) delete next.requester;
  if (!next.submittedFrom) delete next.submittedFrom;
  if (!next.submittedTo) delete next.submittedTo;
  if (!next.requestPages || next.requestPages <= 1) delete next.requestPages;
  return next;
}
